import * as React from "react";
import styled from "@emotion/styled";
import { useParams } from "react-router-dom";
import {
  Avatar,
  Box,
  BrushWrapper,
  Container,
  CopyToClipboard,
  ExpandContent,
  FlexRow,
  H1,
  H2,
  H3,
  Hide,
  IconArrow,
  IconInstagram,
  IconTwitter,
  IconWebsite,
  Link,
  LoadingSpinner,
  RichText,
  ScreenReaderText,
  Stack,
  Text,
} from "atoms";
import { GenericImage } from "features/images";
import {
  getOpenCallTag,
  OpenCallCTA,
  OpenCallDetailsCard,
  OpenCallFAQs,
  OpenCallTagBadge,
  useOpenCall,
  customDescriptions,
  customRequirements,
  useUsersOpenCallVotes,
  type OpenCallLink,
  OpenCallTabs,
  useOpenCallUserSubmissions,
  OpenCallSubmissionSummary,
} from "features/openCalls";
import { useAuth } from "features/auth";
import { Head } from "features/misc";
import { pluralize } from "utils/text";

const inviteMessage = `Check out this open call from HUG - I thought you and your art would be perfect for it! You can learn more and submit your work for free here.`;

function OpenCallDetails() {
  const {
    user,
    roles: { showAdminFeatures },
  } = useAuth();
  const { openCallId } = useParams<{ openCallId: string }>();

  const {
    data: openCall,
    isLoading,
    remove,
    refetch,
  } = useOpenCall(openCallId);

  // Used for "refreshing" this Open Call when the countdown in page heading reaches zero to
  // update its tag to the next state and therefor the rest of the page's style and actions.
  const refetchOpenCall = React.useCallback(() => {
    remove();
    refetch();
  }, [remove, refetch]);

  const { data: userVotes } = useUsersOpenCallVotes();
  const voted = !!userVotes?.openCallIds.includes(openCall?.id ?? "");

  const { data: userSubmissionsResp } = useOpenCallUserSubmissions(
    openCallId,
    user?.id,
  );

  if (isLoading || !openCall) {
    return <LoadingSpinner />;
  }

  const tag = getOpenCallTag(openCall);
  const submissionWindowClosed = !["Submissions Open", "Coming Soon"].includes(
    tag,
  );

  const usersSubmissions = userSubmissionsResp?.data ?? [];
  const canPurchaseSubmissions = !!userSubmissionsResp?.canPurchaseSubmissions;
  const isEligible = !!userSubmissionsResp?.isEligible;

  const userHasSubmitted = !!usersSubmissions?.length;
  const userIsWinner = !!usersSubmissions.some((s) => s.selectedAt);
  const userSubmissionAllowance =
    userSubmissionsResp?.userSubmissionAllowance ?? 1;

  const inviteUrl = window.location.href;

  return (
    <>
      <Head title={`Apply for Artist Open Call | ${openCall.title} | HUG`} />
      <SBrushWrapper>
        <OpenCallImage>
          <GenericImage
            src={openCall.heroImage}
            alt={openCall.heroImage.altText ?? ""}
            sizes="(max-width: 680px) 100vw, 50vw"
          />
        </OpenCallImage>
        <OpenCallTitle width="lg">
          <FlexRow
            flexDirection="column"
            gap="0"
            alignItems="flex-start"
            itemsFlex="auto"
          >
            <BackLink variant="blank" to="/open-calls" size="sm">
              <IconArrow dir="l" /> Open Calls
            </BackLink>
            <OpenCallTitleWidth gap="sm">
              <OpenCallTagBadge
                openCall={openCall}
                submitted={userHasSubmitted}
                voted={voted}
              />
              <H1 size="xxxxl" textTransform="none">
                {openCall.title}
              </H1>
              {showAdminFeatures && (
                <Link
                  size="xxs"
                  variant="default"
                  to={`/admin/open-calls/${openCallId}/edit`}
                >
                  Edit Open Call
                </Link>
              )}
            </OpenCallTitleWidth>
          </FlexRow>
        </OpenCallTitle>
      </SBrushWrapper>

      <Container width="md">
        <Main>
          <FlexRow
            alignItems="flex-start"
            gap="2rem"
            justifyContent="space-between"
            // itemsFlex attribute intentionally empty
            itemsFlex=""
          >
            <LeftCol gap="md">
              <OpenCallDetailsCard
                {...openCall}
                openCallId={openCall.id}
                tag={tag}
                submissionCount={openCall.submissionCount ?? 0}
                maxWinners={openCall.maxWinners ?? 0}
                allowPaidSubmissions={!!openCall.numberOfPaidSubmissions}
                canPurchaseSubmissions={canPurchaseSubmissions}
                userSubmissionAllowance={userSubmissionAllowance}
                isUser={!!user}
                isEligible={isEligible}
                numberSubmitted={usersSubmissions.length}
                refetchOpenCall={refetchOpenCall}
                usersVotes={userVotes?.openCallIds ?? []}
                voted={voted}
                userIsWinner={userIsWinner}
              />

              {userHasSubmitted && (
                <Hide breakpoint="desktop">
                  <H2 size="md">
                    {pluralize(usersSubmissions.length, "My Submission")}
                  </H2>
                  {usersSubmissions.map((sub) => (
                    <OpenCallSubmissionSummary
                      showArtistName={false}
                      userIsSubmitter
                      isSubmissionsOpen={!submissionWindowClosed}
                      showOpenCallLink={false}
                      showCreatedAt={false}
                      {...sub}
                      key={sub.submissionId}
                    />
                  ))}
                </Hide>
              )}

              <div>
                <H2 size="md" id="details">
                  Open Call Details
                </H2>
                <RichText
                  value={
                    customDescriptions[openCall.id] ?? openCall.description
                  }
                />
                {openCall.requirements &&
                  openCall.requirements !== "<p><br></p>" && (
                    <>
                      <H2 size="md">Requirements</H2>
                      <RichText
                        value={
                          customRequirements[openCall.id] ??
                          openCall.requirements
                        }
                      />
                    </>
                  )}
              </div>

              <ShowLarge>
                <OpenCallFAQs
                  isAfterSubmissionWindow={submissionWindowClosed}
                />
              </ShowLarge>
            </LeftCol>

            <OpenCallSidebar gap="sm">
              {userHasSubmitted && (
                <Hide breakpoint="maxDesktop">
                  <Stack gap="sm">
                    <H2 size="md">
                      {pluralize(usersSubmissions.length, "My Submission")}
                    </H2>
                    <Stack gap="sm">
                      {usersSubmissions.map((sub) => (
                        <OpenCallSubmissionSummary
                          showArtistName={false}
                          userIsSubmitter
                          isSubmissionsOpen={!submissionWindowClosed}
                          showOpenCallLink={false}
                          showCreatedAt={false}
                          {...sub}
                          key={sub.submissionId}
                        />
                      ))}
                    </Stack>
                  </Stack>
                </Hide>
              )}

              {!!openCall.partnerName && (
                <Box id="affiliation">
                  <Stack gap="sm">
                    {openCall.partnerLogo?.url && (
                      <FlexRow justifyContent="center">
                        <Avatar
                          src={
                            openCall.partnerLogo?.url300 ??
                            openCall.partnerLogo?.url
                          }
                          size={66}
                        />
                      </FlexRow>
                    )}

                    <H2 textAlign="center" size="md">
                      About {openCall.partnerName}
                    </H2>

                    {(openCall.partnerLinks?.length ?? 0) > 0 && (
                      <FlexRow justifyContent="center" gap="10px">
                        {openCall.partnerLinks?.map((link: OpenCallLink) => (
                          <PartnerLink
                            key={link.url}
                            {...link}
                            partnerName={openCall.partnerName}
                          />
                        ))}
                      </FlexRow>
                    )}

                    {openCall.partnerDescription &&
                      openCall.partnerDescription !== "<p><br></p>" && (
                        <Text as="div" textAlign="center">
                          <ExpandContent
                            chevron={false}
                            showLabel="Read more"
                            hideLabel="Show less"
                            linesVisible={2}
                          >
                            <RichText value={openCall.partnerDescription} />
                          </ExpandContent>
                        </Text>
                      )}
                  </Stack>
                </Box>
              )}

              {!submissionWindowClosed && (
                <Box>
                  <Stack>
                    <H2 size="md" textAlign="center">
                      Share with an Artist
                    </H2>

                    <Text size="xs" textAlign="center">
                      Know someone whose artwork would be perfect for this?
                      Share the opportunity with&nbsp;them!
                    </Text>

                    <Text textAlign="center" as="div">
                      <CopyInviteButton
                        buttonText="Copy Invite Link"
                        value={inviteUrl}
                        sendShareEvent
                      />
                      <br />
                      <br />
                    </Text>

                    <MessageBox gap="sm">
                      <H3 size="sm" textAlign="center">
                        Not sure what to say?
                      </H3>

                      <Text
                        size="xs"
                        textAlign="center"
                        style={{ pointerEvents: "none" }}
                      >
                        <em>&ldquo;{inviteMessage}&rdquo;</em>
                      </Text>

                      <FlexRow alignItems="center" justifyContent="center">
                        <CopyInviteButton
                          buttonText="Copy Link with message"
                          value={`${inviteMessage} ${inviteUrl}`}
                        />
                      </FlexRow>
                    </MessageBox>
                  </Stack>
                </Box>
              )}
            </OpenCallSidebar>
          </FlexRow>

          {!!openCall.submissionCount && (
            <OpenCallTabs openCall={openCall} tag={tag} />
          )}

          <ShowSmall>
            <OpenCallFAQs isAfterSubmissionWindow={submissionWindowClosed} />
          </ShowSmall>
          <OpenCallCTA />
        </Main>
      </Container>
    </>
  );
}

const Main = styled(Stack)({
  padding: "2rem 0 5rem",
}).withComponent("main");
Main.defaultProps = { gap: "xl" };

const LeftCol = styled(Stack)(({ theme }) => ({
  flex: "2 0 400px",
  marginTop: "calc(-2rem + -4rem)",
  [theme.breakpoints.desktopMedium]: {
    "&&": { maxWidth: 660 },
  },
}));

const OpenCallSidebar = styled(Stack)(({ theme }) => ({
  flex: "1 0 350px",
  [theme.breakpoints.desktopMedium]: {
    "&&": { maxWidth: 500 },
  },
}));

function PartnerLink({
  type,
  url,
  partnerName,
}: OpenCallLink & { partnerName?: string }) {
  let Icon = IconWebsite;
  if (type === "twitter") {
    Icon = IconTwitter;
  }
  if (type === "instagram") {
    Icon = IconInstagram;
  }

  return (
    <SLink to={url} target="_blank">
      <ScreenReaderText>
        {type} link for {partnerName}
      </ScreenReaderText>
      <Icon size="20px" />
    </SLink>
  );
}

const SBrushWrapper = styled(BrushWrapper)(({ theme }) => ({
  position: "relative",
  zIndex: 1,
  [theme.breakpoints.tablet]: {
    display: "flex",
    flexWrap: "nowrap",
    alignSelf: "stretch",
  },
  'input[type="text"]': {
    color: theme.colors.fg,
  },
}));

const OpenCallTitle = styled(Container)(({ theme }) => ({
  paddingBottom: "6rem",
  flex: "4",
  [theme.breakpoints.tablet]: {
    paddingBottom: "4rem",
    paddingTop: theme.spacing.md,
    h1: { fontSize: theme.fontSizes.xxl },
  },
  [theme.breakpoints.desktopMedium]: {
    h1: { fontSize: theme.fontSizes.xxxl },
  },
  [theme.breakpoints.desktopLarge]: {
    h1: { fontSize: theme.fontSizes.xxxxl },
  },
}));

const OpenCallTitleWidth = styled(Stack)(({ theme }) => ({
  margin: "1.5rem 0 0 0",
  [theme.breakpoints.tablet]: {
    margin: "clamp(1.5rem, calc(1rem + 5vw), 100px) 0",
    width: "50%",
  },
}));

const BackLink = styled(Link)(({ theme }) => ({
  "&&, &&:hover, &&:focus-visible": {
    color: theme.colors.bg,
    textDecorationColor: theme.colors.bg,
    borderBottom: "2px solid rgba(255,255,255,0)",
  },
  "&&:hover, &&:focus-visible": {
    borderBottom: "2px solid currentColor",
  },
}));

const OpenCallImage = styled.div(({ theme }) => ({
  marginTop: 0,
  aspectRatio: "31/17",
  "&>div": {
    display: "block",
    position: "relative",
    overflow: "hidden",
    objectPosition: "center",
    aspectRatio: "31/17",
    mask: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 400 218"><path d="M400 34V0H250c-31 28-69 56-69 56l15-18c16-17 19-19 34-38H0v60c13 7 31 6 45 0 25-13 67-50 67-50L76 49l-6 8c-29 32-47 48-53 88-2 19 5 33 18 42 11 8 43 10 57 9 29-4 56-17 80-34 31-20 39-24 67-48l4-4-3 5c-17 23-36 73 7 97 26 15 86-1 153-48V49l-44 41s20-30 44-56Z"></path></svg>') top left/contain no-repeat`,
  },
  img: {
    height: "100%",
    objectPosition: "center",
  },
  [theme.breakpoints.tablet]: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: "50%",
    aspectRatio: "auto",
    "&>div": {
      height: "100%",
      aspectRatio: "auto",
      mask: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 600 412"><path d="m785 10 7-10H483l-2 1c-28 22-57 47-84 70l50-61 7-10H17l-2 2a79 79 0 0 0-7 82c11 22 32 33 55 35 23 3 47-2 66-11 25-12 48-26 71-41-18 18-35 36-52 56h-1c-46 54-130 186-53 247 61 48 146 11 198-24 43-29 85-62 124-96-16 25-31 52-41 80-9 27-11 54-6 82h266c32-35 59-74 89-111-25 36-47 71-71 111h259V0h-85l-2 1c-35 28-70 60-98 88l58-79Z"></path></svg>') top left/cover no-repeat`,
    },
  },
}));

const SLink = styled(Link)(({ theme }) => ({
  borderRadius: theme.borderRadius.round,
  boxShadow: theme.boxShadow.light,
  width: 40,
  height: 40,
  display: "flex",
  justifyContent: "center",
  alignContent: "center",
  "&:hover, &:focus-visible": {
    boxShadow: theme.boxShadow.dark,
  },
}));

const CopyInviteButton = styled(CopyToClipboard)(({ theme }) => ({
  marginLeft: "auto",
  marginRight: "auto",
  textAlign: "center",
  display: "inline-block",
  "clipboard-copy": {
    padding: "0.45em 1.2em 0.4em",
    border: `2px solid ${theme.colors.fg}`,
    borderRadius: theme.borderRadius.round,
    backgroundColor: theme.colors.fg,
    color: theme.colors.bg,
    fontSize: theme.fontSizes.xs,
    display: "flex",
  },
  "&:hover clipboard-copy, &:focus clipboard-copy": {
    backgroundColor: theme.colors.bg,
    color: theme.colors.fg,
  },
  svg: {
    order: 99,
  },
}));

const MessageBox = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.colors.accent1LL,
  borderRadius: theme.borderRadius.sm,
  padding: "1.5rem 1rem",
}));

const ShowSmall = styled.div(({ theme }) => ({
  [theme.breakpoints.desktopMedium]: {
    display: "none",
  },
}));

const ShowLarge = styled.div(({ theme }) => ({
  display: "none",
  [theme.breakpoints.desktopMedium]: {
    display: "block",
  },
}));

export default OpenCallDetails;
